/**
 * The contact form's limits, as PR 1's server checks them
 * (`server/src/modules/plugins/help_centre.helpers.ts`). Checking here gives
 * feedback before sending; the server still decides.
 */

export const MAX_SUBJECT_LENGTH = 150;
export const MAX_MESSAGE_LENGTH = 5000;
export const MAX_ATTACHMENTS = 10;
export const MAX_ATTACHMENTS_MB = 25;
export const MAX_ATTACHMENTS_BYTES = MAX_ATTACHMENTS_MB * 1024 * 1024;

const MAX_PHONE_LENGTH = 40;
const PHONE_PATTERN = /^[0-9+()\-. ]+$/;

export const ATTACHMENT_EXTENSIONS = [
	".pdf",
	".doc",
	".docx",
	".xls",
	".xlsx",
	".ppt",
	".pptx",
	".txt",
	".csv",
	".png",
	".jpg",
	".jpeg",
	".eml",
	".msg",
];

const extensionOf = (name: string) => {
	const dot = name.lastIndexOf(".");
	return dot > 0 ? name.slice(dot).toLowerCase() : "";
};

export const totalBytes = (files: { size: number }[]): number =>
	files.reduce((sum, file) => sum + file.size, 0);

/** The attachments with `added` too, or why they can't be added. */
export const checkAttachments = <T extends { name: string; size: number }>(
	current: T[],
	added: T[]
): { files: T[] } | { error: string } => {
	const refused = added.find(
		(file) => !ATTACHMENT_EXTENSIONS.includes(extensionOf(file.name))
	);
	if (refused) {
		return {
			error: `${refused.name} can't be attached. Attach PDF, Word, Excel, PowerPoint, text, CSV, PNG, JPEG, EML or MSG files.`,
		};
	}
	if (current.length + added.length > MAX_ATTACHMENTS) {
		return { error: `You can attach up to ${MAX_ATTACHMENTS} files.` };
	}
	if (totalBytes(current) + totalBytes(added) > MAX_ATTACHMENTS_BYTES) {
		return {
			error: `Attachments can be at most ${MAX_ATTACHMENTS_MB} MB in total.`,
		};
	}
	return { files: [...current, ...added] };
};

/**
 * The profile's phone number as the old form sent it ("+44 7700900123"), or
 * undefined when there's none, or it isn't one the server accepts: sending
 * that would refuse the whole request.
 */
export const profilePhone = (
	user: { country_code?: unknown; phone_number?: unknown } | null | undefined
): string | undefined => {
	const number =
		typeof user?.phone_number === "string" ||
		typeof user?.phone_number === "number"
			? String(user.phone_number).trim()
			: "";
	if (!number) return undefined;
	const code =
		typeof user?.country_code === "string" ? user.country_code.trim() : "";
	const phone = [code, number].filter(Boolean).join(" ");
	return phone.length <= MAX_PHONE_LENGTH && PHONE_PATTERN.test(phone)
		? phone
		: undefined;
};
